import { useCallback, useEffect, useState } from 'react';
import type { AvatarReviewStatus } from '../api/avatar';

const STORAGE_KEY = 'goodtime.playerAvatarCustom';

/** The player's custom (uploaded) avatar, once they have one. {@code url} is the
 *  gated serve URL; {@code status} is PENDING while awaiting review (shown to
 *  the uploader only) or APPROVED once cleared. */
export interface CustomAvatar {
  uploadId: string;
  url: string;
  status: Extract<AvatarReviewStatus, 'PENDING' | 'APPROVED'>;
}

/** Same in-tab pub/sub pattern as {@link usePlayerAvatar} / {@link usePlayerName}
 * so a write from one hook instance (e.g. the picker modal) reaches every live
 * instance (the world-map avatar, the wheel seat) without a remount. */
type Listener = (next: CustomAvatar | null) => void;
const listeners = new Set<Listener>();
function emit(next: CustomAvatar | null): void {
  for (const fn of listeners) fn(next);
}

function readStored(): CustomAvatar | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CustomAvatar) : null;
  } catch {
    return null;
  }
}

/**
 * Read + write the player's custom avatar. v1 persists it client-side and
 * rehydrates from the server on launch (see {@code App} → {@code getAvatarState});
 * the server is authoritative for which custom image is public (approved).
 *
 * @returns tuple of [current, setCurrent]. Setting null drops the custom avatar
 *   so the renderer falls back to the built-in face.
 */
export function usePlayerCustomAvatar(): [CustomAvatar | null, (next: CustomAvatar | null) => void] {
  const [custom, setCustom] = useState<CustomAvatar | null>(() => readStored());

  useEffect(() => {
    listeners.add(setCustom);
    if (typeof window === 'undefined') return () => {
      listeners.delete(setCustom);
    };
    const handler = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setCustom(event.newValue ? (JSON.parse(event.newValue) as CustomAvatar) : null);
    };
    window.addEventListener('storage', handler);
    return () => {
      listeners.delete(setCustom);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const update = useCallback((next: CustomAvatar | null) => {
    if (typeof window !== 'undefined') {
      try {
        if (next === null) window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Quota / private-mode — ignore; in-memory state still updates.
      }
    }
    emit(next);
  }, []);

  return [custom, update];
}
